<!--
  Copyright (c) 2026 237 Solutions Group Co., Ltd.
  Developed by Patcharapong Ponzue (contact@ibig.dev)

  Credit Package Add/Edit Sidebar
-->
<template>
  <b-sidebar
    id="add-new-credit-package-sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0">
          {{ Object.entries(data).length === 0 ? $t("Add") : $t("Edit") }}
          {{ $t("Credit Package") }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>
      <!-- Form -->
      <validation-observer ref="formData">
        <b-form class="p-2">
          <b-row>
            <!-- Name (multi-language) -->
            <b-col cols="12">
              <b-card-text>{{ $t("Name") }}</b-card-text>
            </b-col>
            <b-col
              v-for="(item, index) in language"
              :key="index + 'name'"
              cols="12"
            >
              <b-form-group
                :label="item.name"
                :label-for="'name-' + item.lang"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="'name-' + item.lang"
                  :rules="index === 0 ? 'required' : ''"
                >
                  <b-form-input
                    :id="'name-' + item.lang"
                    v-model="name[item.lang]"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="item.name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Credit Amount -->
            <b-col cols="12">
              <b-form-group
                :label="$t('Credits')"
                label-for="credit"
              >
                <validation-provider
                  #default="{ errors }"
                  name="credit"
                  rules="required"
                >
                  <b-form-input
                    id="credit"
                    v-model="credit"
                    type="number"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t('Credits')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Amount (Price) -->
            <b-col cols="12">
              <b-form-group
                :label="`${$t('Price')} (${$t('Baht')})`"
                label-for="amount"
              >
                <validation-provider
                  #default="{ errors }"
                  name="amount"
                  rules="required"
                >
                  <b-form-input
                    id="amount"
                    v-model="amount"
                    type="number"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t('Price')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Bonus -->
            <b-col cols="12">
              <b-form-group
                :label="$t('Bonus')"
                label-for="bonus"
              >
                <b-form-input
                  id="bonus"
                  v-model="bonus"
                  type="number"
                  :placeholder="$t('Bonus')"
                />
              </b-form-group>
            </b-col>

            <!-- Description (multi-language) -->
            <b-col cols="12">
              <b-card-text>{{ $t("Detail") }}</b-card-text>
            </b-col>
            <b-col
              v-for="(item, index) in language"
              :key="index + 'detail'"
              cols="12"
            >
              <b-form-group
                :label="item.name"
                :label-for="'detail-' + item.lang"
              >
                <b-form-textarea
                  :id="'detail-' + item.lang"
                  v-model="detail[item.lang]"
                  :placeholder="item.name"
                  rows="3"
                />
              </b-form-group>
            </b-col>

            <!-- Status -->
            <b-col cols="12">
              <b-form-group
                :label="$t('Status')"
                label-for="status"
              >
                <b-form-checkbox
                  id="status"
                  v-model="status"
                  switch
                >
                  {{ status ? $t('Active') : $t('Inactive') }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
          <!-- Action button -->
          <div class="d-flex mt-2">
            <b-button
              variant="primary"
              class="mr-2"
              @click="validationForm()"
            >
              {{ $t("Submit") }}
            </b-button>
            <b-button
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              {{ $t("Cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BRow,
  BCol,
  BCardText,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BRow,
    BCol,
    BCardText,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    language: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      required,
      dataId: null,
      name: {},
      credit: 0,
      amount: 0,
      bonus: 0,
      detail: {},
      status: true,
    }
  },
  watch: {
    isSidebarActive(val) {
      if (!val) return
      if (Object.entries(this.data).length === 0) {
        this.initValues()
      } else {
        const {
          _id, name, credit, amount, bonus, description, status,
        } = this.data
        this.dataId = _id
        this.name = this.arrayToObject(name)
        this.credit = credit || 0
        this.amount = amount || 0
        this.bonus = bonus || 0
        this.detail = this.arrayToObject(description)
        this.status = status !== undefined ? status : true
      }
    },
  },
  methods: {
    // แปลง [{lang, value}] → {lang: value}
    arrayToObject(arr) {
      if (!arr) return {}
      if (Array.isArray(arr)) {
        const obj = {}
        arr.forEach(item => {
          if (item.lang) obj[item.lang] = item.value || ''
        })
        return obj
      }
      return { ...arr }
    },
    // แปลง {lang: value} → [{lang, value}]
    objectToArray(obj) {
      if (!obj) return []
      if (Array.isArray(obj)) return obj
      return Object.keys(obj).map(lang => ({ lang, value: obj[lang] }))
    },
    initValues() {
      this.dataId = null
      this.name = {}
      this.credit = 0
      this.amount = 0
      this.bonus = 0
      this.detail = {}
      this.status = true
    },
    validationForm() {
      this.$refs.formData.validate().then(success => {
        if (success) {
          const obj = {
            name: this.objectToArray(this.name),
            credit: Number(this.credit),
            amount: Number(this.amount),
            bonus: Number(this.bonus),
            description: this.objectToArray(this.detail),
            status: this.status,
          }
          if (this.dataId !== null) {
            obj._id = this.dataId
            this.$emit('update', obj)
          } else {
            this.$emit('add', obj)
          }
        }
      })
    },
  },
}
</script>
